import React, { createContext, useCallback, useContext, useEffect, useImperativeHandle, useRef } from 'react';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import styles from './styles.module.scss';
import { doNothing } from '@/libs/utils/function';

export interface CarouselContextProps {
  /**
   * 현재 활성화된 index
   */
  index: number;

  /**
   * 인덱스 설정
   * @param index
   */
  setIndex: (index: number) => void;
}

export interface CarouselProps {
  children?: React.ReactNode;
  infinite?: boolean;
  autoPlay?: boolean;
  padding?: string;
  /**
   * Current active slide index
   */
  activeIndex?: number;
}

export interface CarouselProviderProps {
  children?: React.ReactNode;
}

const CarouselContext = createContext<CarouselContextProps >({
  index: 0,
  setIndex: doNothing,
});

export const useCarousel = () => {
  return useContext(CarouselContext);
};

const Carousel = (props: CarouselProps) => {
  const {
    infinite = false,
    autoPlay = false,
    padding = '',
    children,
  } = props;
  const sliderRef = React.useRef<Slider>(null);
  const carousel = useCarousel();

  // Handle slide change
  const handleAfterChange = useCallback((currentIndex: number) => {
    if(currentIndex < 0) return;
    carousel.setIndex(currentIndex);
  }, [carousel]);

  useEffect(() => {
    if(sliderRef.current) {
      sliderRef.current.slickGoTo(carousel.index ?? 0);
    }
  }, [carousel.index, sliderRef]);

  return <Slider
    ref={sliderRef}
    dots={false}
    infinite={infinite}
    autoplay={autoPlay}
    adaptiveHeight={true}
    initialSlide={carousel.index ?? 0}
    slidesToShow={1}
    slidesToScroll={1}
    arrows={false}
    dotsClass={styles.dots}
    centerMode={!!padding}
    centerPadding={padding}
    afterChange={handleAfterChange}
    waitForAnimate={true}
    speed={500}
  >
    {children}
  </Slider>;
};

export const CarouselProvider = (props: CarouselProviderProps) => {
  const { children } = props;
  const [index, setIndex] = React.useState(0);

  return (
    <CarouselContext.Provider value={{index, setIndex}}>
      <Carousel />
      {children}
    </CarouselContext.Provider>
  )
}

export default Carousel;
